<template>
    <div class="plan-resources mt-page">

        <b-row>
            <b-col cols="8">
                <h1>{{ $i18n.gt('HEADING_PLAN_RESOURCES_FOR') }} {{ planName }}</h1>
            </b-col>
            <b-col cols="4" class="text-right">
                <b-btn class="btn-ghost-blue mr-3"
                       @click="addResource()">
                    <shs-icon name="plus"></shs-icon>
                    {{ $i18n.gt('LABEL_NEW_PLAN_OF_CARE_OPTION') }}
                </b-btn>
                <b-button variant="primary"
                          class="float-right"
                          @click="save()">
                    {{ $i18n.gt('LABEL_SAVE') }}
                </b-button>
            </b-col>
        </b-row>

        <data-table ref="dataTable"
                    :show-filters="true"
                    :show-pager="true"
                    :show-details="false"
                    :get-items="getPlanResources"
                    :filters="filters"
                    :fields="fields">

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key == 'resourceKeysList'">
                    <div class="plan-resources__key-list"
                         v-for="(key,i) in slotProps.row.item.resourceKeysList" :key="i">
                        {{ getResourceKeyHeading(key, widgets) }}
                    </div>
                </div>
                <div v-else-if="slotProps.row.field.key == 'resource_label'">
                    <div v-html="slotProps.row.item.resource_label"></div>
                </div>
                <div v-else-if="slotProps.row.field.key == 'actions'">
                    <b-btn variant="secondary" size="sm" class="btn-icon mb-2"
                           @click="editResource(slotProps.row.item)">
                        <shs-icon name="pencil"></shs-icon>
                    </b-btn>
                </div>
            </template>

        </data-table>

        <!-- edit resource modal-->

        <b-modal hide-footer
                 size="lg"
                 no-close-on-backdrop
                 id="editResourceModal"
                 :title="`Edit Resource`">

            <b-tabs>
                <b-tab title="English">
                    <b-textarea rows="6" v-model="editingItem.resource_label">
                    </b-textarea>
                </b-tab>

                <b-tab title="Spanish">
                    <b-textarea rows="6" v-model="editingItem.resource_label_es">
                    </b-textarea>
                </b-tab>
            </b-tabs>

            <b-card no-body class="mt-3">
                <b-card-header>
                    <h3>{{ $i18n.gt('HEADING_DISPLAY_RESOURCES_PATIENT_INDICATORS') }}</h3>
                </b-card-header>
                <b-card-body>
                    <div v-for="(group,i) in getWidgetGroups()" :key="i">
                        <h4>{{ group.heading }}</h4>
                        <b-form-checkbox-group class="plan-resources__indicator-checkboxes"
                                               :options="getKeyOptions(group.resourceKeys, widgets)"
                                               v-model="editingItem.resourceKeysList">
                        </b-form-checkbox-group>
                    </div>
                </b-card-body>
            </b-card>

            <div class="mt-3 text-right">
                <b-btn variant="danger" class="mr-3"
                       v-if="editingItem.id"
                       @click="deleteResource()">
                    {{ $i18n.gt('LABEL_DELETE_RESOURCE') }}
                </b-btn>
                <b-btn variant="primary"
                       @click="saveResource()">
                    {{ $i18n.gt('LABEL_SAVE') }}
                </b-btn>
            </div>

        </b-modal>

    </div>

</template>

<script>

import Vue from 'vue'
import * as _ from 'lodash'
import ReportMixin from '@/providers/ReportMixin'

/**
 * Page to manage plan of care options for a Plan
 */
export default Vue.extend({
    name: 'PlanResources',
    mixins: [ReportMixin],
    props: {
        planId: Number
    },
    data() {
        const widgets = this.getWidgets() // see ReportMixin
        const keyOptions = this.getKeyOptions(_.keys(widgets), widgets)

        return {
            planName: 'Plan',
            editingItem: {},
            fields: [
                {key: 'resourceKeysList', sortable: true, label: 'Display For'},
                {key: 'resource_label', sortable: false, label: 'Resource Label'},
                {key: 'actions', sortable: false, label: ''},
            ],
            filters: {
                plan_id: {field: 'plan_id', type: 'hidden', value: this.planId},
                resource_keys: {
                    field: 'resource_keys',
                    type: 'select',
                    options: keyOptions,
                    label: 'Display For'
                },
                resource_label: {
                    field: 'resource_label',
                    type: 'string',
                    value: '',
                    label: 'Resource Label'
                },
            },
            widgets: widgets
        }
    },
    methods: {
        getPlanResources(listParams) {
            return this.$api.getItems(this, 'plan-resource', this.filters, listParams)
        },
        getResourceKeyHeading(key, widgets) {
            const widget = widgets[key]
            if (widget) {
                return this.$i18n.gt(widget.headingKey)
            } else {
                return key // shouldn't happen
            }
        },
        getKeyOptions(resourceKeys, widgets) {
            const opts = []
            _.each(resourceKeys, (key) => {
                const widget = widgets[key]
                if (widget) {
                    opts.push({text: this.$i18n.gt(widget.headingKey), value: key})
                } else {
                    // key is e.g. HEALTHY_EATING
                    opts.push({text: this.getResourceKeyHeading(key, widgets), value: key})
                }
            })
            return opts
        },

        addResource() {
            const item = {
                plan_id: this.planId,
                resource_type: 'plan_of_care_option'
            }
            this.editResource(item)
        },
        editResource(item) {
            this.editingItem = item
            this.$bvModal.show('editResourceModal')
        },
        async saveResource() {
            this.editingItem.resource_keys = this.editingItem.resourceKeysList ? this.editingItem.resourceKeysList.join('|') : ''
            if (this.editingItem.id) {
                try {
                    await this.$api.updateItem(this, 'plan-resource', this.editingItem)
                    this.$bvModal.hide('editResourceModal')
                    this.editingItem = {}
                    this.$toasted.show('Resource updated', {duration: 3000})
                    this.$refs.dataTable.refresh()
                } catch (e) {
                    this.$utils.handleError(e, 'Error updating resource')
                }
            } else {
                try {
                    await this.$api.createItem(this, 'plan-resource', this.editingItem)
                    this.$bvModal.hide('editResourceModal')
                    this.editingItem = {}
                    this.$toasted.show('Resource created', {duration: 3000})
                    this.$refs.dataTable.refresh()
                } catch (e) {
                    this.$utils.handleError(e, 'Error creating resource')
                }
            }
        },
        async deleteResource() {
            if (await this.$bvModal.msgBoxConfirm(`Delete this Resource permanently for this Plan?`)) {
                try {
                    await this.$api.deleteItem(this, 'plan-resource', this.editingItem)
                    this.$bvModal.hide('editResourceModal')
                    this.editingItem = {}
                    this.$toasted.show('Resource deleted', {duration: 3000})
                    this.$refs.dataTable.refresh()
                } catch (e) {
                    this.$utils.handleError(e, 'Error deleting resource')
                }
            }
        }
    },
    async mounted() {
        // load plan
        const plan = await this.$api.getItem(this, 'plan', this.planId)
        this.planName = plan.name
    }

})
</script>

<style lang="scss">

.plan-resources {
    .plan-defaults-list {
        max-width: 300px;
    }
}

.plan-resources__key-list {
    white-space: nowrap;
}

.advice-color-key {
    display: inline-block;
    border: solid 1px #ccc;
    height: 15px;
    width: 15px;
}

</style>
